/**
 * ChatModals — All modal dialogs used by the chat.
 *
 * Extracted from ChatContainer to reduce orchestrator size.
 * Includes: MatrixModal, StatusModal, MetadataModal, AttachmentsModal, FileMetadataModal.
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { StatusModal } from "./StatusModal";
import { MatrixModal } from "./MatrixModal";
import MetadataModal from "./MetadataModal";
import AttachmentsModal from "./AttachmentsModal";
import { getAcceptedTypes, isFileTypeAccepted } from "../utils/attachmentValidation";

const ChatModals = ({
  // Matrix modal
  matrixModal,
  onMatrixConfirm,
  onMatrixCancel,
  // Status modal
  statusModal,
  statusMessage,
  onStatusClose,
  // Metadata modal
  allowMetadata,
  metadataHook,
  assistantColors,
  chatMetadataReadOnly,
  // Attachments modal
  attachmentsHook,
  attachmentConfig,
  // File metadata modal
  fileMetadataModal,
  onCloseFileMetadata,
}) => {
  const { t } = useTranslation();

  // Safety validation at the save level: filter files that are not accepted by the assistant.
  // This is a second line of defence in case the modal's own validation is bypassed.
  const acceptedTypes = getAcceptedTypes(attachmentConfig);
  const handleAttachmentsSaveValidated = (data) => {
    const validFiles = data.attachments.filter((file) =>
      isFileTypeAccepted(file, acceptedTypes)
    );
    attachmentsHook.handleAttachmentsSave({ ...data, attachments: validFiles });
  };

  return (
    <>
      <MatrixModal
        visible={matrixModal}
        onConfirm={onMatrixConfirm}
        onCancel={onMatrixCancel}
        assistantColors={assistantColors}
      />

      <StatusModal
        visible={statusModal}
        message={statusMessage}
        onClose={onStatusClose}
      />

      {allowMetadata && (
        <MetadataModal
          open={metadataHook.metadataModal}
          onClose={metadataHook.closeMetadataModal}
          onSave={metadataHook.handleMetadataSave}
          assistantColors={assistantColors}
          chatMetadata={metadataHook.metadata.chat}
          messageMetadata={metadataHook.metadata.message}
          chatMetadataReadOnly={chatMetadataReadOnly}
        />
      )}

      <AttachmentsModal
        open={attachmentsHook.attachmentsModal}
        onClose={attachmentsHook.closeAttachmentsModal}
        onSave={handleAttachmentsSaveValidated}
        assistantColors={assistantColors}
        attachments={attachmentsHook.messageFiles}
        metadataCards={attachmentsHook.filesMetadata}
        fileSpecificMetadata={attachmentsHook.fileSpecificMetadata}
        allowMetadata={allowMetadata}
        attachmentConfig={attachmentConfig}
      />

      {/* File metadata modal (read-only, triggered from FileCard) */}
      {fileMetadataModal.open && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden">
            <div className="px-6 pt-6 pb-2">
              <h3 className="text-lg font-medium text-gray-900">
                {t("Metadata")}
              </h3>
              <p className="text-sm mt-1 text-gray-500">
                {fileMetadataModal.fileName}
              </p>
            </div>
            <div className="p-6 max-h-80 overflow-y-auto">
              {fileMetadataModal.metadata && (
                <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
                  {Object.entries(fileMetadataModal.metadata).map(
                    ([key, values], idx) => (
                      <div
                        key={idx}
                        className="border border-gray-200 rounded-xl bg-gray-50 p-3"
                        style={{ minHeight: "100px" }}
                      >
                        <pre
                          className="font-mono text-xs m-0"
                          style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}
                        >
{`"key": ${JSON.stringify(key)},
"values": [
${(Array.isArray(values) ? values : [values]).map((v) => `  ${JSON.stringify(v)}`).join(",\n")}
]`}
                        </pre>
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
            <div className="px-6 py-4 flex justify-end">
              <button
                type="button"
                onClick={onCloseFileMetadata}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 transition-colors cursor-pointer"
              >
                {t("close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { ChatModals };
export default ChatModals;
